import { assign, createMachine, InterpreterFrom, sendParent } from "xstate-4";
import { useActor } from "@xstate-4/vue";
import { xlog } from "vue-app/utils/xstate";
import axios from "vue-app/utils/axios";
import { Conversation } from "vue-app/pages/machines/machinePageInbox";
import { gtagSend } from "vue-app/plugins/edroneGtag";
import { machineInboxImproveMyResponse } from "vue-app/components/Inbox/machines/machineInboxImproveMyResponse";
import {
  DTOConversationMessageData,
  DTOCustomerInfo,
  DTOInboxAgent,
  DTOInboxConversationActionLog,
  DTOInboxConversationAttachment,
  DTOInboxConversationMessageType,
} from "vue-app/api/dto.d";
import { useFreemiumStore } from "vue-app/components/Freemium/Freemium.store";
import { storeToRefs } from "pinia";
import { postInboxSendMessage } from "vue-app/api/InboxService";

interface InboxAttachment extends File {
  url?: string;
}

type Context = {
  conversation: DTOConversationMessageData[];
  conversationInfo: Conversation | null;
  loadMore: boolean;
  status: "OPEN" | "CLOSED" | "SNOOZED";
  messageValue: string;
  sendingMessageValue: string;
  agentList: DTOInboxAgent[];
  conversationHistory: DTOInboxConversationActionLog[] | null;
  attachmentList: InboxAttachment[];
  sendingAttachmentList: InboxAttachment[];
  drafts: Draft[];
  newDraft: Draft;
  editDraft: Draft;
  draftId: number;
  allegroDiscussionClosureReason: string;
  allegroWriteMessageType: string;
  isAllegroClaimModalOpen: boolean;
};

type Events =
  | { type: "RETRY" }
  | { type: "SELECT_CONVERSATION"; conversationData: Conversation }
  | { type: "UPDATE_CONVERSATION"; conversationData: Conversation }
  | { type: "UPDATE_MESSAGE"; message: string }
  | { type: "TOGGLE_ASSISTANT" }
  | { type: "APPLY_ASSISTANT" }
  | { type: "LOAD_MESSAGES"; loadMore: boolean }
  | { type: "LOAD_AGENTS"; agentList: DTOInboxAgent[] }
  | { type: "SHARE_CONVERSATION"; email: string }
  | { type: "ASSIGN_AGENT"; agentId: number }
  | { type: "UNASSIGN_AGENT" }
  | { type: "CLOSE_CONVERSATION"; status: "OPEN" | "CLOSED" | "SNOOZED" }
  | { type: "SNOOZE_CONVERSATION"; date: number }
  | { type: "OPEN_CONVERSATION"; status: "OPEN" | "CLOSED" | "SNOOZED" }
  | { type: "SEND_MESSAGE"; onEnter?: boolean }
  | { type: "SEND_NOTE"; onEnter?: boolean }
  | { type: "ADD_ATTACHMENT" }
  | { type: "FILE_ADDED"; file: File }
  | { type: "CANCEL" }
  | { type: "REMOVE_ATTACHMENT"; index: number }
  | { type: "OPEN_TEMPLATES_MODAL" }
  | { type: "CLOSE_TEMPLATES_MODAL" }
  | { type: "OPEN_SNOOZE_MODAL" }
  | { type: "CLOSE_SNOOZE_MODAL" }
  | { type: "OPEN_SHARE_MODAL" }
  | { type: "CLOSE_SHARE_MODAL" }
  | { type: "OPEN_ADD_CUSTOMER_MODAL" }
  | { type: "CLOSE_ADD_CUSTOMER_MODAL" }
  | { type: "ADD_CUSTOMER"; email: string }
  | { type: "OPEN_HISTORY_MODAL" }
  | { type: "CLOSE_HISTORY_MODAL" }
  | { type: "GET_HISTORY" }
  | { type: "APPLY_TEMPLATE"; message: string | null }
  | { type: "NEW_TEMPLATE"; content: string }
  | { type: "EDIT_TEMPLATE"; editDraft: Draft }
  | { type: "CLOSE_NEW_TEMPLATE" }
  | { type: "CLOSE_EDIT_TEMPLATE" }
  | { type: "UPDATE_NEW_DRAFT_NAME"; name: string }
  | { type: "UPDATE_NEW_DRAFT_CONTENT"; content: string }
  | { type: "UPDATE_EDIT_DRAFT_NAME"; name: string }
  | { type: "UPDATE_EDIT_DRAFT_CONTENT"; content: string }
  | { type: "SAVE_NEW_DRAFT" }
  | { type: "UPDATE_DRAFT" }
  | { type: "DELETE_TEMPLATE"; draftId: number }
  | { type: "CANCEL_DELETE_TEMPLATE" }
  | { type: "CONFIRM_DELETE_TEMPLATE" }
  | { type: "OPEN_IMPROVE_MY_RESPONSE_MODAL" }
  | { type: "CLOSE_IMPROVE_MY_RESPONSE_MODAL" }
  | { type: "APPLY_IMPROVE_TEXT"; message: string }
  | { type: "OPEN_ALLEGRO_DISPUTE_MODAL" }
  | { type: "CLOSE_ALLEGRO_DISPUTE_MODAL" }
  | { type: "UPDATE_CLOSE_ALLEGRO_DISPUTE_REASON"; reason: string }
  | { type: "CONFIRM_CLOSE_ALLEGRO_DISPUTE" }
  | { type: "UPDATE_ALLEGRO_WRITE_MESSAGE_TYPE"; messageType: string }
  | { type: "OPEN_ALLEGRO_CLAIM_MODAL" }
  | { type: "CLOSE_ALLEGRO_CLAIM_MODAL" }
  | { type: "error.platform.sendMessage"; data: Error };

export enum InboxConversationActionType {
  STATUS_CHANGE = 1,
  RATING_CHANGE,
  ASSIGNED_CHANGE,
}

export enum InboxConversationStatus {
  OPEN,
  CLOSED,
  ARCHIVED,
  SNOOZED,
}

export type conversationMessageData = {
  agentId: number;
  attachments: DTOInboxConversationAttachment[];
  context: string;
  customerId: number | null;
  customerInfo: DTOCustomerInfo | null;
  date: number;
  facebookPageId: number | null;
  inboxConversationId: number;
  inboxConversationMessageType: DTOInboxConversationMessageType;
  read: boolean;
  self: boolean;
  whatsappNumber: number | null;
};

type UploadAttachmentResponse = {
  url: string;
};

export type Draft = {
  agentId: number;
  content: string | null;
  inboxConversationDraftId: number;
  name: string | null;
  trackerId: number;
};

const fileAcceptByPlatform = {
  ALLEGRO: "image/png, image/gif, image/bmp, image/tiff, image/jpeg, application/pdf",
};

export const machineInboxChat = createMachine(
  {
    id: "InboxChat",
    tsTypes: {} as import("./machineInboxChat.typegen").Typegen0,
    schema: {
      events: {} as Events,
      context: {} as Context,
      services: {} as {
        loadMessagesByConversationID: {
          data: DTOConversationMessageData[];
        };
        sendMessage: {
          data: conversationMessageData;
        };
        sendNote: {
          data: conversationMessageData;
        };
        snoozeConversation: {
          data: null;
        };
        openConversation: {
          data: null;
        };
        changingConversationReadStatus: {
          data: Conversation;
        };
        getConversationHistory: {
          data: DTOInboxConversationActionLog[];
        };
        uploadAttachment: {
          data: UploadAttachmentResponse;
        };
        getDrafts: {
          data: Draft[];
        };
      },
    },
    context: {
      conversation: [],
      conversationInfo: null,
      loadMore: false,
      status: "OPEN",
      messageValue: "",
      sendingMessageValue: "",
      agentList: [],
      conversationHistory: null,
      attachmentList: [],
      sendingAttachmentList: [],
      drafts: [],
      newDraft: {
        agentId: 0,
        content: null,
        inboxConversationDraftId: 0,
        name: null,
        trackerId: 0,
      },
      editDraft: {
        agentId: 0,
        content: "",
        inboxConversationDraftId: 0,
        name: "",
        trackerId: 0,
      },
      draftId: 0,
      allegroDiscussionClosureReason: "",
      allegroWriteMessageType: "",
      isAllegroClaimModalOpen: false,
    },
    initial: "empty",
    states: {
      empty: {
        tags: ["empty"],
        entry: "removeIdFromPath",
      },

      idle: {
        tags: ["idle"],

        on: {
          LOAD_MESSAGES: [
            {
              target: "loadMore",
              actions: ["gtag"],
              cond: (context, event) => event.loadMore,
            },
            {
              target: "loadMessages",
            },
          ],

          UNASSIGN_AGENT: [
            {
              actions: ["showIsDemoToast"],
              cond: "isDemo",
            },
            {
              target: "unassignAgent",
              actions: ["gtag"],
              cond: (context) => context.conversationInfo?.assignedAgentId !== null,
            },
          ],

          ASSIGN_AGENT: [
            {
              actions: ["showIsDemoToast"],
              cond: "isDemo",
            },
            {
              target: "assignToAgent",
              actions: ["gtag"],
              cond: (context, event) =>
                context.conversationInfo?.assignedAgentId === null ||
                event.agentId !== context.conversationInfo?.assignedAgentId,
            },
          ],

          CLOSE_CONVERSATION: [
            {
              actions: ["showIsDemoToast"],
              cond: "isDemo",
            },
            {
              target: "closeConversation",
              actions: ["gtag"],
            },
          ],

          TOGGLE_ASSISTANT: {
            target: "conversation.openAssistant",
            cond: "canToggleAssistant",
          },

          UPDATE_MESSAGE: {
            actions: ["assignMessageValue"],
          },

          UPDATE_ALLEGRO_WRITE_MESSAGE_TYPE: {
            actions: ["assignAllegroWriteMessageType"],
          },

          SEND_MESSAGE: [
            {
              actions: ["showIsDemoToast"],
              cond: "isDemo",
            },
            {
              actions: ["showIsFreemiumToast"],
              cond: "isFreemiumAndLimitReached",
            },
            {
              target: "sendingMessage",
              actions: ["gtag"],
              cond: "canSendMessage",
            },
          ],

          SEND_NOTE: [
            {
              actions: ["showIsDemoToast"],
              cond: "isDemo",
            },
            {
              target: "sendingNote",
              actions: ["gtag"],
              cond: "canSendMessage",
            },
          ],

          OPEN_CONVERSATION: [
            {
              actions: ["showIsDemoToast"],
              cond: "isDemo",
            },
            {
              target: "openConversation",
              actions: ["gtag"],
            },
          ],

          SNOOZE_CONVERSATION: [
            {
              actions: ["showIsDemoToast"],
              cond: "isDemo",
            },
            {
              target: "snoozeConversation",
              actions: ["gtag"],
            },
          ],

          ADD_ATTACHMENT: "addingAttachment",

          FILE_ADDED: {
            target: "addingAttachment.uploading",
            actions: ["assignAttachmentToAttachmentList"],
          },

          REMOVE_ATTACHMENT: {
            target: "idle",
            internal: true,
            cond: "thereIsSomeAttachment",
            actions: "removeElementFromAttachmentList",
          },

          OPEN_TEMPLATES_MODAL: {
            target: "templatesModal",
            actions: ["gtag"],
          },

          OPEN_SNOOZE_MODAL: {
            target: "snoozeModal",
          },

          OPEN_SHARE_MODAL: {
            target: "shareModal",
          },

          OPEN_ADD_CUSTOMER_MODAL: {
            target: "addCustomerModal",
          },

          OPEN_HISTORY_MODAL: {
            target: "historyModal",
            actions: ["gtag"],
          },

          OPEN_IMPROVE_MY_RESPONSE_MODAL: {
            target: "improveMyResponseModal",
            cond: "canImproveMyResponse",
          },

          OPEN_ALLEGRO_CLAIM_MODAL: {
            target: "allegroClaimModal",
            actions: assign({ isAllegroClaimModalOpen: () => true }),
          },
        },

        states: {
          closeAllegroDiscussionModal: {
            states: {
              closed: {
                on: {
                  OPEN_ALLEGRO_DISPUTE_MODAL: "open",
                },
              },

              open: {
                tags: ["openAllegroDiscussionModal"],
                on: {
                  CLOSE_ALLEGRO_DISPUTE_MODAL: {
                    target: "closed",
                    actions: "clearAllegroDiscussionClosureReason",
                  },

                  UPDATE_CLOSE_ALLEGRO_DISPUTE_REASON: {
                    target: "open",
                    internal: true,
                    actions: "assignAllegroDiscussionClosureReason",
                  },

                  CONFIRM_CLOSE_ALLEGRO_DISPUTE: "closingAllegroDiscussion",
                },
              },

              closingAllegroDiscussion: {
                invoke: {
                  src: "closeAllegroDiscussion",
                  id: "closeAllegroDiscussion",
                  onDone: {
                    target: "open",
                    actions: ["toastSuccess", "clearAllegroDiscussionClosureReason"],
                  },
                  onError: {
                    target: "open",
                    actions: "toastError",
                  },
                },
              },
            },

            initial: "closed",
          },
        },

        initial: "closeAllegroDiscussionModal",
      },

      loadMessages: {
        tags: ["loadingMessages"],
        invoke: {
          src: "loadMessagesByConversationID",
          id: "loadMessagesByConversationID",
          onDone: [
            {
              target: "changingConversationReadStatus",
              actions: ["assignConversation"],
              cond: (context) => {
                return context.conversationInfo?.read === false;
              },
            },
            {
              target: "idle",
              actions: ["assignConversation"],
            },
          ],
          onError: [
            {
              target: "errorLoadingMessages",
              actions: ["toastError"],
            },
          ],
        },
        on: {
          SELECT_CONVERSATION: {
            actions: ["assignConversationInfo", "assignStatus"],
            target: "loadMessages",
          },
        },
      },

      updateMessages: {
        tags: ["updatingMessages"],
        invoke: {
          src: "updateMessagesByConversationID",
          id: "updateMessagesByConversationID",
          onDone: [
            {
              target: "changingConversationReadStatus",
              actions: ["assignConversation"],
              cond: (context) => {
                return context.conversationInfo?.read === false;
              },
            },
            {
              target: "idle",
              actions: ["assignConversation"],
            },
          ],
          onError: [
            {
              target: "idle",
              actions: ["toastError"],
            },
          ],
        },
        on: {
          SELECT_CONVERSATION: {
            actions: ["assignConversationInfo", "assignStatus"],
            target: "loadMessages",
          },
        },
      },

      loadMore: {
        tags: ["loadingMore"],
        invoke: {
          src: "loadMessagesByConversationID",
          id: "loadMessagesByConversationID",
          onDone: [
            {
              target: "idle",
              actions: ["assignConversation"],
            },
          ],
          onError: [
            {
              target: "idle",
              actions: ["toastError"],
            },
          ],
        },
      },

      sendingMessage: {
        tags: ["sendingMessage"],
        entry: ["clearMessageValue", "copySendingAttachmentList", "clearAttachmentList"],
        invoke: {
          src: "sendMessage",
          id: "sendMessage",
          onDone: [
            {
              actions: ["clearSendingMessageValue", "clearSendingAttachmentList"],
              target: "idle",
            },
          ],
          onError: [
            {
              actions: ["toastError", "restoreSendingMessageValue", "restoreSendingAttachmentList"],
              target: "idle",
            },
          ],
        },
      },

      sendingNote: {
        tags: ["sendingNote"],
        entry: ["clearMessageValue"],
        invoke: {
          src: "sendNote",
          id: "sendNote",
          onDone: [
            {
              actions: ["clearSendingMessageValue"],
              target: "idle",
            },
          ],
          onError: [
            {
              actions: ["toastError", "restoreSendingMessageValue"],
              target: "idle",
            },
          ],
        },
      },

      errorLoadingMessages: {
        tags: ["error"],
        on: {
          RETRY: {
            target: "loadMessages",
          },
        },
      },

      assignToAgent: {
        invoke: {
          src: "assignAgent",
          id: "assignAgent",
          onDone: [
            {
              target: "idle",
            },
          ],
          onError: [
            {
              target: "idle",
              actions: ["toastError"],
            },
          ],
        },
      },

      unassignAgent: {
        invoke: {
          src: "unassignAgent",
          id: "unassignAgent",
          onDone: [
            {
              target: "idle",
            },
          ],
          onError: [
            {
              target: "idle",
              actions: ["toastError"],
            },
          ],
        },
      },

      shareConversation: {
        invoke: {
          src: "shareConversation",
          id: "shareConversation",
          onDone: [
            {
              target: "idle",
              actions: ["toastSuccess"],
            },
          ],
          onError: [
            {
              target: "idle",
              actions: ["toastError"],
            },
          ],
        },
      },

      addCustomer: {
        invoke: {
          src: "addCustomer",
          id: "addCustomer",
          onDone: [
            {
              target: "idle",
              actions: ["toastSuccess"],
            },
          ],
          onError: [
            {
              target: "idle",
              actions: ["toastError"],
            },
          ],
        },
      },

      closeConversation: {
        invoke: {
          src: "closeConversation",
          id: "closeConversation",
          onDone: [
            {
              target: "idle",
              actions: ["assignStatus"],
            },
          ],
          onError: [
            {
              target: "idle",
              actions: ["toastError"],
            },
          ],
        },
      },

      snoozeConversation: {
        invoke: {
          src: "snoozeConversation",
          id: "snoozeConversation",
          onDone: [
            {
              target: "idle",
              actions: ["assignStatus"],
            },
          ],
          onError: [
            {
              target: "idle",
              actions: ["toastError"],
            },
          ],
        },
      },

      openConversation: {
        invoke: {
          src: "openConversation",
          id: "openConversation",
          onDone: [
            {
              target: "idle",
              actions: ["assignStatus"],
            },
          ],
          onError: [
            {
              target: "idle",
              actions: ["toastError"],
            },
          ],
        },
      },

      conversation: {
        // Kamil TODO: Editor in machine instead of component
        initial: "editorIdle",
        states: {
          editorIdle: {
            description: "Note/message",
            on: {
              TOGGLE_ASSISTANT: {
                target: "openAssistant",
              },
            },
          },
          openAssistant: {
            tags: ["openAssistant"],
            on: {
              APPLY_ASSISTANT: {
                target: "editorIdle",
              },
              TOGGLE_ASSISTANT: {
                target: "editorIdle",
              },
            },
          },
        },
      },

      changingConversationReadStatus: {
        invoke: {
          src: "changingConversationReadStatus",
          id: "changingConversationReadStatus",
          onDone: [
            {
              target: "idle",
              actions: [sendParent((context, event) => ({ type: "MARK_AS_READ", conversationData: event.data }))],
            },
          ],
          onError: [
            {
              target: "idle",
            },
          ],
        },
      },

      addingAttachment: {
        states: {
          systemDialog: {
            on: {
              FILE_ADDED: {
                target: "uploading",
                actions: ["assignAttachmentToAttachmentList"],
              },

              CANCEL: "#InboxChat.idle",
            },

            invoke: {
              src: "systemFileDialog",
              id: "systemFileDialog",
            },
          },

          uploading: {
            invoke: {
              src: "uploadAttachment",
              id: "uploadAttachment",
              onDone: [
                {
                  target: "#InboxChat.allegroClaimModal",
                  cond: (context) => context.isAllegroClaimModalOpen,
                  actions: "assignAttachmentIdToAttachmentListElement",
                },
                {
                  target: "#InboxChat.idle",
                  actions: "assignAttachmentIdToAttachmentListElement",
                },
              ],

              onError: {
                target: "#InboxChat.idle",
                actions: ["toastError", "removeLastElementFromAttachmentList"],
              },
            },

            on: {
              REMOVE_ATTACHMENT: {
                target: "#InboxChat.idle",
                cond: "thereIsSomeAttachment",
                actions: "removeElementFromAttachmentList",
              },
            },
          },
        },

        initial: "systemDialog",
      },

      templatesModal: {
        tags: ["templatesModal"],
        exit: ["clearNewDraft"],

        on: {
          CLOSE_TEMPLATES_MODAL: "idle",
        },

        states: {
          getDrafts: {
            tags: ["getDrafts"],
            invoke: {
              src: "getDrafts",
              id: "getDrafts",
              onDone: {
                target: "idle",
                actions: "assignDrafts",
              },
              onError: {
                target: "idle",
                actions: "toastError",
              },
            },
          },

          idle: {
            tags: ["drafts"],
            on: {
              APPLY_TEMPLATE: {
                target: "#InboxChat.idle",
                actions: ["assignMessageValue", "gtag"],
              },

              NEW_TEMPLATE: [
                {
                  target: "newTemplateModal",
                  actions: "assignNewDraftContent",
                  cond: "isContentProvided",
                },
                {
                  target: "newTemplateModal",
                },
              ],
              EDIT_TEMPLATE: {
                target: "editTemplateModal",
                actions: "assignEditDraft",
              },
              DELETE_TEMPLATE: {
                target: "deleteNotify",
                actions: "assignDraftId",
              },
            },
          },

          newTemplateModal: {
            tags: ["newDraft"],

            on: {
              CLOSE_NEW_TEMPLATE: {
                target: "idle",
              },

              UPDATE_NEW_DRAFT_NAME: {
                target: "newTemplateModal",
                internal: true,
                actions: "assignNewDraftName",
              },

              UPDATE_NEW_DRAFT_CONTENT: {
                target: "newTemplateModal",
                internal: true,
                actions: "assignNewDraftContent",
              },

              SAVE_NEW_DRAFT: {
                target: "saveNewDraft",
                cond: "newDraftIsNotEmpty",
              },
            },
          },

          editTemplateModal: {
            tags: ["editDraft"],
            on: {
              CLOSE_EDIT_TEMPLATE: {
                target: "idle",
                actions: "clearEditDraft",
              },

              UPDATE_EDIT_DRAFT_CONTENT: {
                target: "editTemplateModal",
                internal: true,
                actions: "assignEditDraftContent",
              },

              UPDATE_EDIT_DRAFT_NAME: {
                target: "editTemplateModal",
                internal: true,
                actions: "assignEditDraftName",
              },

              UPDATE_DRAFT: {
                target: "updateDraft",
                cond: "editDraftIsNotEmpty",
              },
            },
          },

          saveNewDraft: {
            invoke: {
              src: "saveNewDraft",
              id: "saveNewDraft",
              onDone: {
                target: "getDrafts",
                actions: "clearNewDraft",
              },
              onError: {
                target: "getDrafts",
                actions: "toastError",
              },
            },
          },

          deleteNotify: {
            tags: ["deleteDraft"],
            on: {
              CANCEL_DELETE_TEMPLATE: {
                target: "idle",
                actions: "clearDraftId",
              },
              CONFIRM_DELETE_TEMPLATE: "deleteDraft",
            },
          },

          deleteDraft: {
            invoke: {
              src: "deleteDraft",
              id: "deleteDraft",
              onDone: {
                target: "getDrafts",
                actions: "clearDraftId",
              },
              onError: {
                target: "getDrafts",
                actions: "toastError",
              },
            },
          },

          updateDraft: {
            invoke: {
              src: "updateDraft",
              id: "updateDraft",

              onDone: {
                target: "getDrafts",
                actions: "clearEditDraft",
              },

              onError: {
                target: "getDrafts",
                actions: "toastError",
              },
            },
          },
        },

        initial: "getDrafts",
      },

      snoozeModal: {
        tags: ["snoozeModal"],
        on: {
          CLOSE_SNOOZE_MODAL: "idle",
          SNOOZE_CONVERSATION: [
            {
              actions: ["showIsDemoToast"],
              cond: "isDemo",
            },
            {
              target: "snoozeConversation",
              actions: ["gtag"],
            },
          ],
        },
      },

      shareModal: {
        tags: ["shareModal"],
        on: {
          CLOSE_SHARE_MODAL: "idle",
          SHARE_CONVERSATION: [
            {
              actions: ["showIsDemoToast"],
              cond: "isDemo",
            },
            {
              target: "shareConversation",
              actions: ["gtag"],
            },
          ],
        },
      },

      addCustomerModal: {
        tags: ["addCustomerModal"],
        on: {
          CLOSE_ADD_CUSTOMER_MODAL: "idle",
          ADD_CUSTOMER: [
            {
              actions: ["showIsDemoToast"],
              cond: "isDemo",
            },
            {
              target: "addCustomer",
              actions: ["gtag"],
            },
          ],
        },
      },

      historyModal: {
        tags: ["historyModal"],
        initial: "idle",
        states: {
          idle: {
            on: {
              GET_HISTORY: {
                target: "gettingConversationHistory",
              },
            },
          },
          gettingConversationHistory: {
            tags: ["gettingConversationHistory"],
            entry: ["clearConversationHistory"],
            invoke: {
              src: "getConversationHistory",
              id: "getConversationHistory",
              onDone: [
                {
                  target: "idle",
                  actions: ["assignConversationHistory"],
                },
              ],
              onError: [
                {
                  target: "idle",
                  actions: ["toastError"],
                },
              ],
            },
          },
        },
        on: {
          CLOSE_HISTORY_MODAL: "idle",
        },
      },

      improveMyResponseModal: {
        tags: ["improveMyResponseModal"],
        invoke: {
          id: "improveMyResponse",
          src: "improveMyResponse",
          data: {
            text: (context: Context) => context.messageValue,
            conversationId: (context: Context) => context.conversationInfo?.inboxConversationId,
          },
        },
        on: {
          CLOSE_IMPROVE_MY_RESPONSE_MODAL: "idle",
          APPLY_IMPROVE_TEXT: {
            actions: ["assignMessageValue"],
            target: "idle",
            cond: (context, event) => Boolean(event.message),
          },
        },
      },

      allegroClaimModal: {
        tags: ["openAllegroClaimModal"],
        on: {
          CLOSE_ALLEGRO_CLAIM_MODAL: "idle",
          // Allow editor actions while modal is open
          UPDATE_MESSAGE: {
            actions: ["assignMessageValue"],
          },
          UPDATE_ALLEGRO_WRITE_MESSAGE_TYPE: {
            actions: ["assignAllegroWriteMessageType"],
          },
          ADD_ATTACHMENT: "addingAttachment",
          FILE_ADDED: {
            target: "addingAttachment.uploading",
            actions: ["assignAttachmentToAttachmentList"],
          },
          REMOVE_ATTACHMENT: {
            target: "allegroClaimModal",
            internal: true,
            cond: "thereIsSomeAttachment",
            actions: "removeElementFromAttachmentList",
          },
          OPEN_TEMPLATES_MODAL: {
            target: "templatesModal",
          },
          SEND_MESSAGE: [
            {
              actions: ["showIsDemoToast"],
              cond: "isDemo",
            },
            {
              actions: ["showIsFreemiumToast"],
              cond: "isFreemiumAndLimitReached",
            },
            {
              target: "sendingMessage",
              actions: ["gtag"],
              cond: "canSendMessage",
            },
          ],
        },
      },
    },
    on: {
      SELECT_CONVERSATION: [
        {
          actions: [
            "clearConversation",
            "clearAttachmentList",
            "assignConversationInfo",
            "assignStatus",
            "assignMessageValue",
            "clearConversationHistory",
          ],
          target: "loadMessages",
          cond: (context, event) => Boolean(event.conversationData),
        },
        {
          target: "empty",
        },
      ],
      UPDATE_CONVERSATION: {
        actions: ["assignConversationInfo", "assignStatus"],
        target: "updateMessages",
      },
      LOAD_AGENTS: {
        actions: ["assignAgentList"],
      },
    },
    predictableActionArguments: true,
    preserveActionOrder: true,
  },
  {
    actions: {
      removeIdFromPath: () => {
        const url = new URL(window.location.href);
        url.searchParams.delete("conversationId");
        window.history.replaceState({}, "", url.toString());
      },
      assignAllegroDiscussionClosureReason: assign({
        allegroDiscussionClosureReason: (context, event) => {
          xlog({ label: "assignAllegroDiscussionClosureReason", type: "action", context, event });
          return event.reason;
        },
      }),
      clearAllegroDiscussionClosureReason: assign({
        allegroDiscussionClosureReason: (context, event) => {
          xlog({ label: "clearAllegroDiscussionClosureReason", type: "action", context, event });
          return "";
        },
      }),
      removeElementFromAttachmentList: assign({
        attachmentList: (context, event) => {
          xlog({ label: "removeElementFromAttachmentList", type: "action", context, event });
          return context.attachmentList.filter((_, index) => index !== event.index);
        },
      }),
      assignAttachmentToAttachmentList: assign({
        attachmentList: (context, event) => {
          xlog({ label: "assignAttachmentToAttachmentList", type: "action", context, event });
          return [...context.attachmentList, event.file];
        },
      }),
      assignAttachmentIdToAttachmentListElement: assign({
        attachmentList: (context, event) => {
          xlog({ label: "assignAttachmentIdToAttachmentListElement", type: "action", context, event });
          const lastAttachment = context.attachmentList[context.attachmentList.length - 1];
          lastAttachment.url = event.data.url;
          return [...context.attachmentList];
        },
      }),
      removeLastElementFromAttachmentList: assign({
        attachmentList: (context, event) => {
          xlog({ label: "removeLastElementFromAttachmentList", type: "action", context, event });
          return context.attachmentList.slice(0, -1);
        },
      }),
      clearAttachmentList: assign({
        attachmentList: (context, event) => {
          xlog({ label: "clearAttachmentList", type: "action", context, event });
          return [];
        },
      }),
      assignConversationInfo: assign({
        conversationInfo: (context, event) => {
          xlog({
            label: "assignConversationInfo",
            type: "action",
            context,
            event,
          });
          if (event.conversationData.inboxConversationId) {
            const url = new URL(window.location.href);
            url.searchParams.set("conversationId", String(event.conversationData.inboxConversationId));
            window.history.replaceState({}, "", url.toString());
          }
          return event.conversationData;
        },
        allegroWriteMessageType: (context, event) => {
          xlog({ label: "clearAllegroWriteMessageType", type: "action", context, event });
          return "";
        },
      }),
      assignConversationHistory: assign({
        conversationHistory: (context, event) => {
          xlog({
            label: "assignConversationHistory",
            type: "action",
            context,
            event,
          });
          return event.data || [];
        },
      }),
      assignConversation: assign({
        conversation: (context, event) => {
          xlog({
            label: "assignConversation",
            type: "action",
            context,
            event,
          });
          return event.data as DTOConversationMessageData[];
        },
        loadMore: (context, event) => {
          xlog({ label: "assignLoadMore", type: "action", context, event });
          if (Array.isArray(event.data) && event.data.length) {
            return !(event.data.length % 50);
          }
          return false;
        },
      }),
      assignStatus: assign({
        status: (context, event) => {
          xlog({ label: "assignStatus", type: "action", context, event });
          if (event.type === "done.invoke.closeConversation") {
            return "CLOSED";
          } else if (event.type === "done.invoke.snoozeConversation") {
            return "SNOOZED";
          } else if (event.type === "done.invoke.openConversation") {
            return "OPEN";
          }
          return event.conversationData.inboxConversationStatus as "OPEN" | "CLOSED" | "SNOOZED";
        },
      }),
      assignAgentList: assign({
        agentList: (context, event) => {
          xlog({ label: "assignAgentList", type: "action", context, event });
          return event.agentList;
        },
      }),
      assignMessageValue: assign({
        messageValue: (context, event) => {
          xlog({ label: "assignMessageValue", type: "action", context, event });
          if (event.type === "SELECT_CONVERSATION") {
            return "";
          }
          return event.message || "";
        },
      }),
      assignAllegroWriteMessageType: assign({
        allegroWriteMessageType: (context, event) => {
          xlog({ label: "assignAllegroWriteMessageType", type: "action", context, event });
          return event.messageType;
        },
      }),
      clearConversation: assign({
        conversation: (context, event) => {
          xlog({ label: "clearConversation", type: "action", context, event });
          return [];
        },
        loadMore: () => {
          return false;
        },
      }),
      clearMessageValue: assign({
        sendingMessageValue: (context, event) => {
          xlog({ label: "assignSendingMessageValue", type: "action", context, event });
          return context.messageValue;
        },
        messageValue: (context, event) => {
          xlog({ label: "clearMessageValue", type: "action", context, event });
          return "";
        },
      }),
      copySendingAttachmentList: assign({
        sendingAttachmentList: (context, event) => {
          xlog({ label: "copySendingAttachmentList", type: "action", context, event });
          return context.attachmentList;
        },
      }),
      clearSendingMessageValue: assign({
        sendingMessageValue: (context, event) => {
          xlog({ label: "clearSendingMessageValue", type: "action", context, event });
          return "";
        },
      }),
      clearSendingAttachmentList: assign({
        sendingAttachmentList: (context, event) => {
          xlog({ label: "clearSendingAttachmentList", type: "action", context, event });
          return [];
        },
      }),
      restoreSendingMessageValue: assign({
        messageValue: (context, event) => {
          xlog({ label: "restoreSendingMessageValue", type: "action", context, event });
          return context.sendingMessageValue;
        },
        sendingMessageValue: (context, event) => {
          xlog({ label: "restoreSendingMessageValue", type: "action", context, event });
          return "";
        },
      }),
      restoreSendingAttachmentList: assign({
        attachmentList: (context, event) => {
          xlog({ label: "restoreSendingAttachmentList", type: "action", context, event });
          return context.sendingAttachmentList;
        },
        sendingAttachmentList: (context, event) => {
          xlog({ label: "restoreSendingAttachmentList", type: "action", context, event });
          return [];
        },
      }),
      clearConversationHistory: assign({
        conversationHistory: (context, event) => {
          xlog({ label: "clearConversationHistory", type: "action", context, event });
          return null;
        },
      }),
      toastSuccess: (context, event) => {
        switch (event.type) {
          case "done.invoke.shareConversation": {
            window.showToast(window._trans._inbox.inboxChat.notifications.shareSuccess, "success");
            break;
          }
          case "done.invoke.addCustomer": {
            window.showToast(window._trans._inbox.inboxChat.notifications.addCustomerSuccess, "success");
            break;
          }
          default: {
            window.showToast(window._trans._inbox.inboxChat.notifications.success, "success");
          }
        }
      },
      toastError: (context, event) => {
        switch (event.type) {
          case "error.platform.uploadAttachment": {
            window.showToast(window._trans._inbox.inboxChat.notifications.attachmentError, "error");
            break;
          }
          case "error.platform.getConversationHistory": {
            window.showToast(window._trans._inbox.inboxChat.notifications.historyError, "error");
            break;
          }
          case "error.platform.sendMessage": {
            if (event.data.message === "FREEMIUM_LIMIT_REACHED") {
              window.showToast(window._trans._freemium.toasts.error.limitReached, "error");
            } else {
              window.showToast(window._trans._inbox.inboxChat.notifications.historyError, "error");
            }
            break;
          }
          default: {
            if (event.data instanceof Error) {
              window.showToast(event.data.message.replaceAll("_", " "), "error");
            }
          }
        }
      },
      showIsDemoToast: (context, event) => {
        xlog({ label: "showIsDemoToast", type: "action", context, event });
        window.showToast(window._trans._inbox.inboxDemo.disabled, "info");
      },
      showIsFreemiumToast: (context, event) => {
        xlog({ label: "showIsDemoToast", type: "action", context, event });
        window.showToast(
          {
            message: window._trans._freemium.messagesLimitToast.title,
            desc: window._trans._freemium.messagesLimitToast.description,
            link: {
              text: window._trans._freemium.notification.upgrade,
              buttonType: "solid",
              onClick: () => {
                window.pubSub.publish("freemium.upgrade");
              },
            },
          },
          "warn"
        );
      },
      assignDrafts: assign({
        drafts: (context, event) => {
          xlog({ label: "assignDrafts", type: "action", context, event });
          return event.data;
        },
      }),
      assignNewDraftContent: assign({
        newDraft: (context, event) => {
          xlog({ label: "assignNewDraftContent", type: "action", context, event });
          return {
            ...context.newDraft,
            content: event.content,
          };
        },
      }),
      assignNewDraftName: assign({
        newDraft: (context, event) => {
          xlog({ label: "assignNewDraftName", type: "action", context, event });
          return {
            ...context.newDraft,
            name: event.name,
          };
        },
      }),
      clearNewDraft: assign({
        newDraft: (context, event) => {
          xlog({ label: "clearNewDraft", type: "action", context, event });
          return {
            ...context.newDraft,
            content: null,
            name: null,
          };
        },
      }),
      assignDraftId: assign({
        draftId: (context, event) => {
          xlog({ label: "assignDraftId", type: "action", context, event });
          return event.draftId;
        },
      }),
      clearDraftId: assign({
        draftId: (context, event) => {
          xlog({ label: "clearDraftId", type: "action", context, event });
          return 0;
        },
      }),
      assignEditDraft: assign({
        editDraft: (context, event) => {
          xlog({ label: "assignEditDraft", type: "action", context, event });
          return event.editDraft;
        },
      }),
      assignEditDraftContent: assign({
        editDraft: (context, event) => {
          xlog({ label: "assignEditDraftContent", type: "action", context, event });
          return {
            ...context.editDraft,
            content: event.content,
          };
        },
      }),
      assignEditDraftName: assign({
        editDraft: (context, event) => {
          xlog({ label: "assignEditDraftName", type: "action", context, event });
          return {
            ...context.editDraft,
            name: event.name,
          };
        },
      }),
      clearEditDraft: assign({
        editDraft: (context, event) => {
          xlog({ label: "clearEditDraft", type: "action", context, event });
          return {
            ...context.editDraft,
            content: "",
            name: "",
          };
        },
      }),
      gtag: (context, event) => {
        xlog({ label: "gtag", type: "action", context, event });
        switch (event.type) {
          case "LOAD_MESSAGES":
            gtagSend({
              event: "button_click",
              component_type: "secondary",
              component_variant: "default",
              component_icon_name: "no_icon",
              component_state: "active",
              component_label: "edrone",
              functionality_group: "inbox",
              functionality: "inbox",
              view: "chat",
              text: "load_more",
            });
            break;
          case "UNASSIGN_AGENT":
            gtagSend({
              event: "select",
              component_icon_name: "user-slash",
              component_state: "active",
              component_label: "edrone",
              functionality_group: "inbox",
              functionality: "inbox",
              view: "chat",
              text: "unassign_agent",
            });
            break;
          case "ASSIGN_AGENT":
            gtagSend({
              event: "select",
              component_icon_name: "user-check",
              component_state: "active",
              component_label: "edrone",
              functionality_group: "inbox",
              functionality: "inbox",
              view: "chat",
              text: "assign_agent",
            });
            break;
          case "CLOSE_CONVERSATION":
            gtagSend({
              event: "button_click",
              component_type: "secondary",
              component_variant: "default",
              component_icon_name: "message-check",
              component_state: "active",
              component_label: "edrone",
              functionality_group: "inbox",
              functionality: "inbox",
              view: "chat",
              text: "close_conversation",
            });
            break;
          case "OPEN_CONVERSATION":
            gtagSend({
              event: "button_click",
              component_type: "secondary",
              component_variant: "default",
              component_icon_name: "arrow-rotate-left",
              component_state: "active",
              component_label: "edrone",
              functionality_group: "inbox",
              functionality: "inbox",
              view: "chat",
              text: "open_conversation",
            });
            break;
          case "SNOOZE_CONVERSATION":
            gtagSend({
              event: "select",
              component_icon_name: "no_icon",
              component_state: "active",
              component_label: "edrone",
              functionality_group: "inbox",
              functionality: "inbox",
              view: "chat",
              text: "snooze_conversation",
            });
            break;
          case "SHARE_CONVERSATION":
            gtagSend({
              event: "button_click",
              component_type: "primary",
              component_variant: "default",
              component_icon_name: "no_icon",
              component_state: "active",
              component_label: "edrone",
              functionality_group: "inbox",
              functionality: "inbox",
              view: "chat",
              text: "share_conversation",
            });
            break;
          case "ADD_CUSTOMER":
            gtagSend({
              event: "button_click",
              component_type: "primary",
              component_variant: "default",
              component_icon_name: "no_icon",
              component_state: "active",
              component_label: "edrone",
              functionality_group: "inbox",
              functionality: "inbox",
              view: "chat",
              text: "add_customer",
            });
            break;
          case "OPEN_HISTORY_MODAL":
            gtagSend({
              event: "select",
              component_icon_name: "clock-rotate-left",
              component_state: "active",
              component_label: "edrone",
              functionality_group: "inbox",
              functionality: "inbox",
              view: "chat",
              text: "show_chat_history",
            });
            break;
          case "OPEN_TEMPLATES_MODAL":
            gtagSend({
              event: "button_click",
              component_type: "secondary",
              component_variant: "default",
              component_icon_name: "plus",
              component_state: "active",
              component_label: "edrone",
              functionality_group: "inbox",
              functionality: "inbox",
              view: "chat",
              text: "open_templates_modal",
            });
            break;
          case "APPLY_TEMPLATE":
            gtagSend({
              event: "select",
              component_icon_name: "no_icon",
              component_state: "active",
              component_label: "edrone",
              functionality_group: "inbox",
              functionality: "inbox",
              view: "chat",
              text: "selected_template",
            });
            break;
          case "SEND_MESSAGE":
            gtagSend({
              event: "button_click",
              component_type: "primary",
              component_variant: "default",
              component_icon_name: "paper-plane",
              component_state: "active",
              component_label: "edrone",
              functionality_group: "inbox",
              functionality: "inbox",
              view: "chat",
              text: "send_message",
            });
            break;
          case "SEND_NOTE":
            gtagSend({
              event: "button_click",
              component_type: "secondary",
              component_variant: "default",
              component_icon_name: "note-sticky",
              component_state: "active",
              component_label: "edrone",
              functionality_group: "inbox",
              functionality: "inbox",
              view: "chat",
              text: "send_note",
            });
            break;
          default:
            break;
        }
      },
    },
    guards: {
      thereIsSomeAttachment: (context, event) => {
        xlog({ label: "thereIsSomeAttachment", type: "guard", context, event });
        return context.attachmentList.length > 0;
      },
      canToggleAssistant: () => {
        return false;
      },
      canSendMessage: (context, event) => {
        xlog({ label: "canSendMessage", type: "guard", context, event });
        if (event.onEnter === false) {
          return false;
        }
        return !!context.messageValue && context.messageValue.length > 0;
      },
      isDemo: () => {
        xlog({ label: "isDemo", type: "guard" });
        return window.EDRONE_CONTEXT.isDemo;
      },
      isFreemiumAndLimitReached: (context) => {
        xlog({ label: "isFreemiumAndLimitReached", type: "guard" });
        const freemiumStore = useFreemiumStore();
        const { usedMessageCredits, paying, limitMessageCredits } = storeToRefs(freemiumStore);
        return (
          !paying.value &&
          usedMessageCredits.value >= limitMessageCredits.value &&
          !!context.messageValue &&
          context.messageValue.length > 0
        );
      },
      editDraftIsNotEmpty: (context, event) => {
        xlog({ label: "editDraftIsNotEmpty", type: "guard", context, event });
        return Boolean(
          (context.editDraft.content && context.editDraft.content.length > 0) ||
            (context.editDraft.name && context.editDraft.name.length > 0)
        );
      },
      newDraftIsNotEmpty: (context, event) => {
        xlog({ label: "newDraftIsNotEmpty", type: "guard", context, event });
        return Boolean(
          context.newDraft.content &&
            context.newDraft.content.length > 0 &&
            context.newDraft.name &&
            context.newDraft.name.length > 0
        );
      },
      isContentProvided: (context, event) => {
        xlog({ label: "isContentProvided", type: "guard", context, event });
        return Boolean(event.content);
      },
      canImproveMyResponse: (context, event) => {
        xlog({ label: "canImproveMyResponse", type: "guard", context, event });
        return Boolean(context.messageValue);
      },
    },
    services: {
      closeAllegroDiscussion: async (context) => {
        xlog({ label: "closeAllegroDiscussion", type: "action", context });
        if (!context.conversationInfo?.inboxConversationId) {
          throw new Error("No conversation id");
        }

        return await postInboxSendMessage(
          context.conversationInfo?.inboxConversationId,
          context.allegroDiscussionClosureReason
        );
      },
      systemFileDialog: (context) => (cb) => {
        const input = document.createElement("input");
        input.type = "file";
        if (context.conversationInfo?.sourceType) {
          const x = fileAcceptByPlatform[context.conversationInfo?.sourceType];
          if (x) {
            input.accept = x;
          }
        }
        // @ts-ignore Note: "oncancel" actually works
        input.oncancel = () => {
          cb({ type: "CANCEL" });
        };
        input.onchange = () => {
          const file = input.files?.[0];
          if (!file) {
            throw new Error("No file selected");
          }
          if (file.size / 1024 / 1024 >= 2) {
            window.parent.showToast(window._trans._inbox.inboxChat.notifications.maxFileSize + " " + "2 MB", "error");
            cb({ type: "CANCEL" });
          } else {
            cb({ type: "FILE_ADDED", file });
          }
        };
        input.click();
      },
      uploadAttachment: async (context, event) => {
        xlog({ label: "uploadAttachment", context, event });
        if (context.attachmentList.length === 0 || !context.conversationInfo?.inboxConversationId) {
          throw new Error("No attachment or conversation id");
        }
        const lastAttachment = context.attachmentList[context.attachmentList.length - 1];
        const formData = new FormData();
        formData.append("attachment_file", lastAttachment, lastAttachment.name);
        const response = await axios.post<UploadAttachmentResponse>("/inbox/attachment", formData);
        if (response.data.url === undefined) {
          // api validator
          throw new Error();
        }
        return response.data;
      },
      loadMessagesByConversationID: async (context, event) => {
        xlog({ label: "loadMessagesByConversationID", type: "action", context, event });
        const messages: DTOConversationMessageData[] = [];
        if (event.type === "LOAD_MESSAGES" && event.loadMore) {
          const response = await axios.get<{ data: DTOConversationMessageData[] }>(
            `/inbox/get-messages?inbox_conversation_id=${context.conversationInfo?.inboxConversationId}&from_id=${context.conversation[0].inboxConversationMessageId}&direction=before`
          );
          messages.push(...response.data.data);
        } else {
          const response = await axios.get<{ data: DTOConversationMessageData[] }>(
            `/inbox/get-messages?inbox_conversation_id=${context.conversationInfo?.inboxConversationId}&direction=before`
          );
          messages.push(...response.data.data);
        }
        return [...messages, ...context.conversation];
      },
      updateMessagesByConversationID: async (context, event) => {
        xlog({ label: "updateMessagesByConversationID", type: "action", context, event });
        const response = await axios.get(
          `/inbox/get-messages?inbox_conversation_id=${context.conversationInfo?.inboxConversationId}&from_id=${
            context.conversation[context.conversation.length - 1].inboxConversationMessageId
          }&direction=after`
        );
        return [...context.conversation, ...response.data.data];
      },
      sendMessage: async (context, event) => {
        xlog({ label: "sendMessage", type: "action", context, event });
        const payload: any = {
          inbox_conversation_id: context.conversationInfo?.inboxConversationId,
          content: context.sendingMessageValue,
          inbox_conversation_attachment_base64_urls: context.sendingAttachmentList
            .map((attachment) => btoa(encodeURIComponent(attachment.url || "")))
            .join(","),
        };

        if (context.allegroWriteMessageType) {
          payload.allegro_write_message_type = context.allegroWriteMessageType;
        }
        const response = await axios.post("/inbox/send-message", payload);
        if (response.data.data.status !== "SUCCESS") {
          throw new Error(response.data.data.status);
        }
        return response.data.data;
      },
      sendNote: async (context, event) => {
        xlog({ label: "sendNote", type: "action", context, event });
        const response = await axios.post("/inbox/add-note", {
          inbox_conversation_id: context.conversationInfo?.inboxConversationId,
          content: context.sendingMessageValue,
        });
        return response.data.data;
      },
      closeConversation: async (context, event) => {
        xlog({ label: "closeConversation", type: "action", context, event });
        const response = await axios.post("/inbox/close-conversation", {
          inbox_conversation_id: context.conversationInfo?.inboxConversationId,
        });
        return response.data.data;
      },
      snoozeConversation: async (context, event) => {
        xlog({ label: "snoozeConversation", type: "action", context, event });
        const response = await axios.post("/inbox/snooze-conversation", {
          inbox_conversation_id: context.conversationInfo?.inboxConversationId,
          due_date: event.date,
        });
        return response.data.data;
      },
      openConversation: async (context, event) => {
        xlog({ label: "openConversation", type: "action", context, event });
        const response = await axios.post("/inbox/open-conversation", {
          inbox_conversation_id: context.conversationInfo?.inboxConversationId,
        });
        return response.data.data;
      },
      unassignAgent: async (context, event) => {
        xlog({ label: "unassignAgent", type: "action", context, event });
        const response = await axios.post("/inbox/assign-conversation-to-agent", {
          inbox_conversation_id: context.conversationInfo?.inboxConversationId,
        });
        return response.data.data;
      },
      assignAgent: async (context, event) => {
        xlog({ label: "assignAgent", type: "action", context, event });
        const response = await axios.post("/inbox/assign-conversation-to-agent", {
          inbox_conversation_id: context.conversationInfo?.inboxConversationId,
          agent_id: event.agentId,
        });
        return response.data.data;
      },
      shareConversation: async (context, event) => {
        xlog({ label: "shareConversation", type: "action", context, event });
        const response = await axios.post("/inbox/share-conversation", {
          inbox_conversation_id: context.conversationInfo?.inboxConversationId,
          email: event.email,
        });
        return response.data.data;
      },
      addCustomer: async (context, event) => {
        xlog({ label: "addCustomer", type: "action", context, event });
        const response = await axios.post("/inbox/add-customer-to-conversation", {
          inbox_conversation_id: context.conversationInfo?.inboxConversationId,
          email: event.email,
        });
        return response.data.data;
      },
      changingConversationReadStatus: async (context, event) => {
        xlog({ label: "changingConversationReadStatus", context, event });
        if (!context.conversationInfo?.read) {
          await axios.post("/json/inbox/markAsRead", {
            inbox_conversation_id: context.conversationInfo?.inboxConversationId,
            read: true,
          });
        }
        return { ...context.conversationInfo, read: true } as Conversation;
      },
      getConversationHistory: async (context, event) => {
        xlog({ label: "getConversationHistory", context, event });
        const response = await axios.get(
          `/inbox/get-conversation-history?inbox_conversation_id=${context.conversationInfo?.inboxConversationId}`
        );
        return response.data.data;
      },
      getDrafts: async (context, event) => {
        xlog({ label: "getDrafts", context, event });
        const response = await axios.get(`/json/inbox/getDrafts`);
        return response.data.data;
      },
      saveNewDraft: async (context, event) => {
        xlog({ label: "saveNewDraft", context, event });
        const response = await axios.post(`/json/inbox/saveDraft`, {
          name: context.newDraft.name,
          content: context.newDraft.content,
        });
        return response.data.data;
      },
      deleteDraft: async (context, event) => {
        xlog({ label: "deleteDraft", context, event });
        const response = await axios.post(`/json/inbox/removeDraft`, {
          draftId: context.draftId,
        });
        return response.data.data;
      },
      updateDraft: async (context, event) => {
        xlog({ label: "updateDraft", context, event });
        const response = await axios.post(`/json/inbox/updateDraft`, {
          agent_id: context.editDraft.agentId,
          inbox_conversation_draft_id: context.editDraft.inboxConversationDraftId,
          name: context.editDraft.name,
          content: context.editDraft.content,
        });
        return response.data.data;
      },
      improveMyResponse: machineInboxImproveMyResponse,
    },
  }
);

export type InboxChatActor = InterpreterFrom<typeof machineInboxChat>;

// @ts-ignore
export function useInboxChat(service) {
  return useActor(service.children.get("chat") as InboxChatActor);
}
